import * as Adaptive from "adaptivecards";
import { createRoot } from 'react-dom/client';
import React from "react";
import { Provider as FluentUIThemeProvider } from "@fluentui/react-northstar";
import { Input } from "adaptivecards";
import { BooleanPropertyEditor, ChoicePropertyEditor, IVersionedChoice, PropertySheet, PropertySheetCategory, StringPropertyEditor, TypedCardElementPeer } from "adaptivecards-designer";
import { handleValidationElement, initCustomRenderedElement, removeOtherPropertyConfigurations } from "../Utils/CustomElementUtils";
import { AdaptiveCardTexts, PeoplePicker_UserSourceOptions, PeoplePicker_UserSourceOptionsLabels} from "../../../shared/utils/constants";
import { AffectedUsersPeoplePicker } from "./AffectedUsersPeoplePicker";
import { RequesterPeoplePicker } from "./RequesterPeoplePicker";
import { searchUsers } from "../../../shared/cache/PlatformUserCache";
import _ from "lodash";
import { graphService } from "../../../shared/services/graph.service";
import { TikitPeoplePicker } from "../../../shared/components/PeoplePicker/TikitPeoplePicker";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const selectionModeProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "selectionMode", true);
const sourceProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "source", true);
const displayChoicesProperty = new Adaptive.EnumProperty(Adaptive.Versions.v1_0, "displayChoices", []);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);

export const renderACPeoplePicker = (self: AffectedUsersPeoplePicker | RequesterPeoplePicker | CardPeoplePicker, valueChange: (input: Input) => void, disableField: boolean): HTMLElement => {
  let renderedElement = initCustomRenderedElement();

  const renderElement = (self: AffectedUsersPeoplePicker | RequesterPeoplePicker | CardPeoplePicker, element: HTMLDivElement, valueChange: (input: Input) => void, selectedUsers: any[], disableField?: boolean) => {
    const onSelectedUser = async (selectedUsers: PlatformUser[]) => {
      if (selectedUsers.length > 0) {
        const userList = [];
        for (let u of selectedUsers)
          userList.push(await searchUsers(u));   
        const values = userList.map(i => { return { FullName: i.FullName, UserName: i.UserName, AadObjectId: i.AadObjectId, Id: i.Id } });
        self.valueField = JSON.stringify(values);

        handleValidationElement(element);
      } else
        self.valueField = "";

      valueChange?.(self);
    };
 
    const domNode = document.createElement('div');
    const root = createRoot(domNode);
    root.render(
      // This root is detached from the app's Provider, so northstar components (Avatar's ui-avatar/ui-label) need their own theme context to render consistently.
      <FluentUIThemeProvider theme={CardPeoplePicker.globalTheme} styles={{ background: 'transparent' }}>
        <TikitPeoplePicker
          value={selectedUsers ?? []}
          isMultiple={self.selectionMode === "multiple"}
          {...disableField && { isDisabled: true }}
          onChange={async (selectedUsers) => await onSelectedUser(selectedUsers)}
          globalTheme={CardPeoplePicker.globalTheme}
          source={parseInt(self.source ?? `${PeoplePicker_UserSourceOptions.EntraAll}`)}
          />
      </FluentUIThemeProvider>
    );

    element.append(domNode);
  }

  let selectedUsers = []; let renderAsync = false;
  if (self.valueField && self.valueField.length > 0) {
    try {
      const parsedValue: any[] = JSON.parse(self.valueField);
      selectedUsers = parsedValue.map((i: any) => ({ FullName: i.FullName, UserName: i.UserName, AadObjectId: i.AadObjectId, Id: i.Id }));
    } catch {
      const populateUsers = async (values: string[]) => {
        const graph = new graphService();
        const pUsers = [];
        for (let value of values) {
          let user = await searchUsers({ AadObjectId: value } as PlatformUser, false);
          if (!user) {
            const gu = (await graph.getUser(value))?.data;
            user = gu ? { FullName: gu.displayName, UserName: gu.userPrincipalName, AadObjectId: gu.id } as PlatformUser : undefined;
          }

          pUsers.push(user);
        }

        const selectedUsers = _.compact(pUsers).map((i: any) => ({ FullName: i.FullName, UserName: i.UserName, AadObjectId: i.AadObjectId, Id: i.Id }));
        renderElement(self, renderedElement, valueChange, selectedUsers, disableField);
      };

      const valueStrings = self.valueField.split(",");
      if (valueStrings.length > 0) {
        renderAsync = true;
        populateUsers(valueStrings);
      }
    }
  }

  if (!renderAsync)
    renderElement(self, renderedElement, valueChange, selectedUsers, disableField);

  return renderedElement;
}

export const CardPeoplePickerChoices = Object.values(PeoplePicker_UserSourceOptions).filter(v => isNaN(Number(v))).map((key) => 
  {
    return { name: PeoplePicker_UserSourceOptionsLabels[key], value: `${PeoplePicker_UserSourceOptions[key]}` } as IVersionedChoice;
  });
export class CardPeoplePicker extends Adaptive.TextInput {
  static readonly displayLabelProperty = displayLabelProperty;
  static readonly selectionModeProperty = selectionModeProperty;
  static readonly sourceProperty = sourceProperty;
  static readonly displayChoicesProperty = displayChoicesProperty;
  static readonly requireFieldProperty = requireFieldProperty;
  static readonly JsonTypeName = "People Picker";
  static readonly defaultLabel = "People Picker";
  static pickerChoices: any[];
  static onCustomValueChange: (input: Input) => void;
  static disableField: boolean;
  static globalTheme: any;
  static usersInRole: any[];

  //#region Schema

  @Adaptive.property(displayLabelProperty)
  get displayLabel(): string | undefined {
    return this.getValue(CardPeoplePicker.displayLabelProperty);
  }

  set displayLabel(value: string | undefined) {
    this.setValue(CardPeoplePicker.displayLabelProperty, value);
    this.label = value;
    this.updateLayout();
  }

  @Adaptive.property(selectionModeProperty)
  get selectionMode(): string | undefined {
    return this.getValue(CardPeoplePicker.selectionModeProperty);
  }

  set selectionMode(value: string | undefined,) {
    this.setValue(CardPeoplePicker.selectionModeProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(sourceProperty)
  get source(): string | undefined {
    return this.getValue(CardPeoplePicker.sourceProperty);
  }

  set source(value: string | undefined,) {
    this.setValue(CardPeoplePicker.sourceProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(displayChoicesProperty)
  get displayChoices(): any[] | undefined {
    return this.getValue(CardPeoplePicker.displayChoicesProperty);
  }

  set displayChoices(value: any[] | undefined,) {
    this.setValue(CardPeoplePicker.displayChoicesProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(requireFieldProperty)
  get requireField(): boolean | undefined {
    return this.getValue(CardPeoplePicker.requireFieldProperty);
  }

  set requireField(value: boolean | undefined) {
    this.setValue(CardPeoplePicker.requireFieldProperty, value);
    this.setValue(CardPeoplePicker.isRequiredProperty, value);
    if (value)
      this.setValue(CardPeoplePicker.errorMessageProperty, AdaptiveCardTexts.REQUIRE_FIELD);
    this.updateLayout();
  }

  get valueField(): string | undefined {
    return this.getValue(CardPeoplePicker.valueProperty);
  }

  set valueField(value: string | undefined) {
    this.setValue(CardPeoplePicker.valueProperty, value);
  }

  //#endregion

  protected internalRender(): HTMLElement | undefined {
    if ((this?.displayLabel ?? "").length == 0)
      this.displayLabel = CardPeoplePicker.defaultLabel;

    if (CardPeoplePicker.pickerChoices) 
      this.displayChoices = CardPeoplePicker.pickerChoices;

    let selectMode = "multiple";
    if ((this?.selectionMode ?? "").length == 0)
      this.selectionMode = selectMode;

    let source = `${PeoplePicker_UserSourceOptions.EntraMembers}`;
    if ((this?.source ?? "").length == 0)
      this.source = source;

    return renderACPeoplePicker(this, CardPeoplePicker.onCustomValueChange, CardPeoplePicker.disableField);
  }

  get value(): string {
    return this.getValue(CardPeoplePicker.valueProperty);
  }

  getJsonTypeName(): string {
    return CardPeoplePicker.JsonTypeName;
  }

  updateLayout(processChildren: boolean = true) {
    super.updateLayout(processChildren);
  }
}
  
export class CardPeoplePickerPeer extends TypedCardElementPeer<CardPeoplePicker> {
  static readonly displayLabelProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayLabel", "Label", false);
  static readonly selectionModeProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "selectionMode", "Selection", [{ name: 'Single', value: 'single' }, { name: 'Multiple', value: 'multiple' }] as IVersionedChoice[]);
  static sourceProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "source", "Source", CardPeoplePickerChoices);
  static requireFieldProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "requireField", "Required", false);

  static updateChoices(choices: IVersionedChoice[]) {
    CardPeoplePickerPeer.sourceProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "source", "Source", choices);
  }

  populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {

    super.populatePropertySheet(propertySheet, defaultCategory);
    removeOtherPropertyConfigurations(propertySheet);

    propertySheet.add(
      defaultCategory,
      CardPeoplePickerPeer.idProperty,
      CardPeoplePickerPeer.displayLabelProperty,
      CardPeoplePickerPeer.requireFieldProperty,
      CardPeoplePickerPeer.selectionModeProperty,
      CardPeoplePickerPeer.sourceProperty
    );

  }
}

